import React, { useState, useCallback, useMemo, useEffect } from "react";
import DatePicker, {
    ReactDatePickerProps,
    registerLocale
} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { areMatchingDates } from "helpers/time";
import dayjs from "dayjs";
import { enUS, es } from "date-fns/locale";

interface DateTimePickerProps {
    id: string;
    value?: Date | string;
    error?: string;
    dateOnly?: boolean;
    onChange(
        date: Date | [Date, Date] | null,
        event: React.SyntheticEvent<any, Event> | undefined
    ): void;
    datePickerProps?: Partial<ReactDatePickerProps>;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
    id,
    value,
    error = "",
    dateOnly,
    onChange,
    datePickerProps
}: DateTimePickerProps) => {
    const currentDate = useMemo<Date>(() => new Date(), []);
    const twentyMinutesFromNow = useMemo<Date>(
        () => dayjs(currentDate).add(20, "minute").toDate(),
        [currentDate]
    );
    const startingTime = useMemo(() => {
        if (!value) return currentDate;
        if (typeof value === "string") return new Date(value);
        return value;
    }, [currentDate, value]);

    const getMinTime = useCallback(() => {
        const startValueMinutes = startingTime.getMinutes();
        const nextValidStartMinutes = Math.ceil(startValueMinutes / 5) * 5;

        if (nextValidStartMinutes < 60) {
            if (startValueMinutes % 5 === 0) return startingTime;
            return dayjs(startingTime)
                .add(nextValidStartMinutes, "minute")
                .toDate();
        }

        return new Date(
            startingTime.getFullYear(),
            startingTime.getMonth(),
            startingTime.getDate(),
            startingTime.getHours() + 1,
            0
        );
    }, [startingTime]);

    const [minTime, setMinTime] = useState<Date>(twentyMinutesFromNow);
    const [maxTime, setMaxTime] = useState<Date>(
        new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate(),
            23,
            59
        )
    );

    const handleChange = useCallback(
        (selectedDate: Date, e: React.SyntheticEvent) => {
            const firstMinuteOfSelectedDate = new Date(
                selectedDate.getFullYear(),
                selectedDate.getMonth(),
                selectedDate.getDate(),
                0,
                0
            );
            const lastMinuteOfSelectedDate = new Date(
                selectedDate.getFullYear(),
                selectedDate.getMonth(),
                selectedDate.getDate(),
                23,
                59
            );
            const currentTimeOnSelectedDate = new Date(
                selectedDate.getFullYear(),
                selectedDate.getMonth(),
                selectedDate.getDate(),
                currentDate.getHours(),
                currentDate.getMinutes()
            );

            // reset the available times in the time picker for new date before invoking callback
            if (areMatchingDates(currentDate, selectedDate)) {
                setMinTime(twentyMinutesFromNow);
            } else if (
                datePickerProps.maxDate &&
                areMatchingDates(selectedDate, datePickerProps.maxDate)
            ) {
                setMinTime(firstMinuteOfSelectedDate);
                setMaxTime(currentTimeOnSelectedDate);
            } else {
                setMinTime(firstMinuteOfSelectedDate);
                setMaxTime(lastMinuteOfSelectedDate);
            }

            // invoke the onChange callback passed down as a prop
            onChange(selectedDate, e);
        },
        [currentDate, datePickerProps.maxDate, onChange, twentyMinutesFromNow]
    );

    useEffect(() => {
        // Set the locale for the date picker based on the provided locale prop
        if (datePickerProps.locale === "es") {
            registerLocale("es", es);
        } else {
            // Default to English (locale "en-US")
            registerLocale("en-US", enUS);
        }
    }, [datePickerProps.locale]);

    return (
        <>
            <div className="form-group datetime-picker">
                <DatePicker
                    id={id}
                    selected={getMinTime()}
                    dateFormat={`MM/dd/yyyy${dateOnly ? "" : " h:mm a"}`}
                    onChange={(date, event) => {
                        // Only process changes from the date picker UI
                        // Skip processing changes from manual input (these will be handled by onChangeRaw)
                        // (event is undefined when a "time" is selected)
                        if ((event && event.type !== "change") || !event) {
                            handleChange(date, event);
                        }
                    }}
                    onChangeRaw={(event) => {
                        const inputValue = event.target.value;

                        // Check if input is a complete date matching our date format (MM/dd/yyyy h:mm a)
                        // Formats: MM/dd/yyyy or MM/dd/yyyy h:mm a|AM|PM
                        const dateRegex = dateOnly
                            ? /^\d{2}\/\d{2}\/\d{4}$/
                            : /^\d{2}\/\d{2}\/\d{4}\s+\d{1,2}:\d{2}\s+(am|pm|AM|PM)$/;

                        if (dateRegex.test(inputValue)) {
                            const dateValue = new Date(inputValue);
                            if (
                                dateValue instanceof Date &&
                                !isNaN(dateValue.getTime())
                            ) {
                                // Only submit if it's a valid and complete date
                                handleChange(dateValue, event);
                            }
                        }
                    }}
                    showTimeSelect={!dateOnly}
                    timeIntervals={5}
                    minTime={minTime}
                    maxTime={maxTime}
                    minDate={currentDate}
                    customInput={
                        <input
                            title={id}
                            type="text"
                            className="form-control"
                            value={getMinTime()?.toString()}
                        />
                    }
                    {...datePickerProps}
                />
                <small
                    id={`${id}-help`}
                    aria-describedby={id}
                    className="form-text text-danger"
                >
                    {error}
                </small>
            </div>
        </>
    );
};
